import ScrollPane from "../../../../../lib/html5-software-library/src/ranj/components/scrolling/ScrollPane";
import ChatMessageContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/ChatMessageContent";
import TextContent from "../../../../../lib/html5-software-library/src/ranj/eng/content/TextContent";
import Rectangle from "../../../../../lib/html5-software-library/src/ranj/geom/Rectangle";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import ChatInteractionEvent from "../events/ChatInteractionEvent";
import ChatConversation from "../model/ChatConversation";

export default class ChatOptionsPanelMediator extends AbstractMediator {
    constructor(target, dispatcher, chatModel) {
        super(target, dispatcher);

        this._chatModel = chatModel;

        this._npcId = this.getCustomData().npcId;

        /** @type {ChatConversation} */
        this._conversation = this._chatModel.getConversationForNpcId(this._npcId);
    }

    initialize() {
        super.initialize();

        this._initialY = this._target.y;

        const scrollPaneBounds = this._target.input.nominalBounds;
        const viewport = new Rectangle(0, 0, scrollPaneBounds.width, scrollPaneBounds.height);
        this._scrollPane = new ScrollPane(viewport, null, null, this._target.input.scrollContainer, null, true, true);

        this._target.input.scrollContainer.textField.text = "";

        this._initializeOptionButtons();
        this._showOptions();

        this._sendButton = this.createButton(this._target.sendButton, this._handleSendButton.bind(this));
        this._updateSendButton();

        this.registerEventListener(ChatInteractionEvent.NEW_PLAYER_OPTIONS_AVAILABLE, this._showOptions.bind(this));

        this._target.visible = this.hasAvailableOptions();
    }

    _initializeOptionButtons() {
        if (this._optionButtons) {
            console.warn(`Option buttons are already initialized. Skipping initialization...`);
            return;
        }

        this._optionButtons = [];
        for (let i = 1; i <= ChatOptionsPanelMediator.OPTION_BUTTON_AMOUNT; i++) {
            const skin = this._target[`option${i}`];
            if (!skin) {
                break;
            }

            const button = this.createButton(skin, this._handleOptionButton.bind(this));
            button.setVisible(false);
            this._optionButtons.push(button);
        }

        this._optionButtonHeight = this._optionButtons[0].skin.getBounds().height;
    }

    _showOptions() {
        if (!this.hasAvailableOptions) {
            return;
        }

        for (let i = 0; i < Math.min(this._conversation.currentOptions.length, this._optionButtons.length); i++) {
            const dialogOption = this._conversation.currentOptions[i];
            const dialogContentPackage = dialogOption.getData().getData();
            /** @type {ChatMessageContent} */
            const dialogOptionContent = dialogContentPackage.content;
            const optionButton = this._optionButtons[i];
            optionButton.customData = {
                dialogOption
            };

            const shortText = dialogOptionContent.getShortText().trim();
            optionButton.setLabel(shortText);
            optionButton.setVisible(true);
        }

        for (let j = Math.min(this._conversation.currentOptions.length, this._optionButtons.length); j < this._optionButtons.length; j++) {
            const optionButton = this._optionButtons[j];
            optionButton.customData = {};
            optionButton.setVisible(false);
        }

        this._target.y = this._initialY + (ChatOptionsPanelMediator.OPTION_BUTTON_AMOUNT - this._conversation.currentOptions.length) * this._optionButtonHeight;
    }

    _handleOptionButton(evt) {
        const button = evt.target;
        const customData = button.customData;
        const dialogOption = customData.dialogOption;
        const dialogContentPackage = dialogOption.getData().getData();
        /** @type {ChatMessageContent} */
        const dialogOptionContent = dialogContentPackage.content;

        this._selectedOption = dialogOption;
        this._updateSendButton();

        this._currentTypeWriter = TextTypeWriterAnimation.animate(this._target.input.scrollContainer.textField, dialogOptionContent.getText().trim(), .65, 0,
            null, null, () => {
                this._updateScrollPane();
            });
    }

    _handleSendButton(evt) {
        this._conversation.contentPlayer.optionChosen(this._selectedOption);

        if (this._currentTypeWriter) {
            this._currentTypeWriter.forceFinish();
        }

        this._target.input.scrollContainer.textField.text = "";
        this._showOptions();

        this._selectedOption = null;
        this._updateSendButton();

        this.hide();
    }

    _updateSendButton() {
        this._sendButton.setEnabled(!!this._selectedOption);
    }

    _updateScrollPane() {
        const contentBounds = this._target.input.scrollContainer.getBounds();
        const heightBound = contentBounds.y + contentBounds.height;
        this._scrollPane.setHeightBound(heightBound);
        this._scrollPane.setScrollPosition(0, -heightBound);
    }

    getHeight() {
        const optionButtonCorrection = (ChatOptionsPanelMediator.OPTION_BUTTON_AMOUNT - this._conversation.currentOptions.length) * this._optionButtonHeight;
        return this._target.nominalBounds.height - optionButtonCorrection;
    }

    hasAvailableOptions() {
        return this._conversation.currentOptions.length > 0;
    }

    hide() {
        this._target.visible = false;
    }

    show() {
        this._target.visible = true;
    }
}

ChatOptionsPanelMediator.OPTION_BUTTON_AMOUNT = 4;